<script setup lang="ts">
    import {
        AlertDialog,
        AlertDialogAction,
        AlertDialogCancel,
        AlertDialogContent,
        AlertDialogDescription,
        AlertDialogFooter,
        AlertDialogHeader,
        AlertDialogTitle,
        AlertDialogTrigger,
    } from '@/components/ui/alert-dialog';
    import { useI18n } from 'vue-i18n';
    import { useMutation } from '@tanstack/vue-query';
    import axios from 'axios';
    import { toast } from 'vue-sonner';
    import type { Payroll, Employee } from '@/types';
    import { Button } from '@/components/ui/button';
    import { ChevronRight } from 'lucide-vue-next';
    import { router } from '@inertiajs/vue3';
    import { computed } from 'vue';

    const { t } = useI18n();

    const props = defineProps<{
        payroll: Payroll;
        employees: Employee[];
    }>();

    const { mutate, isPending } = useMutation({
        mutationFn: () =>
            axios.post(
                route('v2.salary.payrolls.approve', {
                    payrollId: props.payroll.id,
                }),
            ),
        onSuccess: (data) => {
            toast.success(data.data.success || t('forms.general.success'));
            router.reload();
        },
        onError: (error: any) => {
            console.log('error', error);
            toast.error(error.response.data.error || t('forms.system_error'));
        },
    });

    const employeesWithoutStatus = computed(() => {
        return props.employees.filter((employee) => !employee.status);
    });
</script>

<template>
    <AlertDialog>
        <AlertDialogTrigger as-child>
            <Button
                variant="ghost_primary"
                size="xs"
                width="narrow"
            >
                {{ t('payrolls.edit.approve') }}
                <ChevronRight class="text-primary" />
            </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
            <AlertDialogHeader>
                <AlertDialogTitle>{{ t('payrolls.edit.approve') }}</AlertDialogTitle>
                <AlertDialogDescription
                    v-if="!!employeesWithoutStatus.length"
                    class="flex flex-col gap-2"
                >
                    {{ t('payrolls.changes.confirm_1') }}
                    <span class="font-bold">
                        {{ employeesWithoutStatus.map((employee) => employee.name).join(', ') }}
                    </span>
                    {{ t('payrolls.changes.confirm_2') }}
                </AlertDialogDescription>
                <AlertDialogDescription v-else>
                    {{ t('salary.js.confirm') }}
                </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
                <AlertDialogCancel>{{ t('salary.js.no') }}</AlertDialogCancel>
                <AlertDialogAction
                    variant="default"
                    @click="mutate"
                >
                    {{ t('salary.js.yes') }}
                </AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
</template>
